import { jsPDF } from "jspdf";

// Builds a clean, text-based PDF of the completed application and returns it
// as a Blob (so the caller can base64-encode it for the email attachment).
//
// Input shape:
// {
//   fields: { organisation, address, charity, name, position, tel, email,
//             website, fundingrequested, liability, safeguarding, about,
//             project, timeframe, outreach, demand, impact, evaluation },
//   expenses: [{ Expenditure, Details, Cost }],
//   income:   [{ Income, Confirmed, Amount }],
//   totalExpense, totalIncome, confirmedIncome, pendingIncome,
// }

const TOP_MARGIN = 56;
const BOTTOM_MARGIN = 50;
const SIDE_MARGIN = 40;
const FONT = "helvetica";

const lineHeightFor = (size) => size * 1.3;

const formatMoney = (value) => {
  const n = Number(value || 0);
  if (Number.isNaN(n)) return `£${value ?? 0}`;
  return Number.isInteger(n)
    ? `£${n.toLocaleString("en-GB")}`
    : `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const generateApplicationPdf = ({
  fields = {},
  expenses = [],
  income = [],
  totalExpense = 0,
  totalIncome = 0,
  confirmedIncome = 0,
  pendingIncome = 0,
} = {}) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const left = SIDE_MARGIN;
  const right = pageWidth - SIDE_MARGIN;
  const contentWidth = right - left;

  let y = TOP_MARGIN;

  const ensureSpace = (needed = 0) => {
    if (y + needed > pageHeight - BOTTOM_MARGIN) {
      doc.addPage();
      y = TOP_MARGIN;
    }
  };

  const writeLine = (line, { size = 11, style = "normal", x = left, align = "left" } = {}) => {
    ensureSpace(lineHeightFor(size));
    doc.setFont(FONT, style);
    doc.setFontSize(size);
    doc.setTextColor(30);
    doc.text(line, x, y, { align });
    y += lineHeightFor(size);
  };

  // Multi-line, word-wrapped block.
  const writeBlock = (str, { size = 11, style = "normal", x = left, maxWidth = contentWidth, gapAfter = 4 } = {}) => {
    const lines = doc.splitTextToSize(String(str == null ? "" : str), maxWidth);
    lines.forEach((line) => writeLine(line, { size, style, x }));
    y += gapAfter;
  };

  // Section heading with an underline, kept together with the first content line.
  const section = (title) => {
    ensureSpace(lineHeightFor(13) + 18);
    y += 14; // gap above the heading
    const baseline = y;
    writeLine(title, { size: 13, style: "bold" });
    doc.setDrawColor(170);
    const lineY = baseline + 4; // sit the underline just below the heading text
    doc.line(left, lineY, right, lineY);
    y = lineY + 12; // leave a gap below the line before the next line of text
  };

  // A labelled value: bold label on the left, wrapped value indented beside it.
  const field = (label, value, { labelWidth = 140, indent = left + 150 } = {}) => {
    const valueLines = doc.splitTextToSize(
      String(value == null || value === "" ? "—" : value),
      right - indent
    );
    const lh = lineHeightFor(11);
    valueLines.forEach((line, i) => {
      ensureSpace(lh);
      doc.setFont(FONT, "bold");
      doc.setFontSize(11);
      doc.setTextColor(30);
      if (i === 0) doc.text(label, left, y);
      doc.setFont(FONT, "normal");
      doc.text(line, indent, y);
      y += lh;
    });
  };

  // Generic wrapped table for the budget sections.
  const table = (columns, rows, { totalLabel, totalValue } = {}) => {
    const lh = lineHeightFor(10);
    const drawRow = (cells, { bold = false } = {}) => {
      const wrapped = cells.map((cell, i) =>
        doc.splitTextToSize(String(cell == null ? "" : cell), columns[i].width - 6)
      );
      const rowCount = Math.max(1, ...wrapped.map((w) => w.length));
      ensureSpace(lh * rowCount + 4);
      const startY = y;
      wrapped.forEach((lines, i) => {
        doc.setFont(FONT, bold ? "bold" : "normal");
        doc.setFontSize(10);
        doc.setTextColor(30);
        lines.forEach((line, li) => {
          doc.text(line, columns[i].x, startY + li * lh, {
            align: columns[i].align || "left",
          });
        });
      });
      // Draw the divider just below the row's text, then leave enough room
      // that the next row's ascenders clear the line (y is a text baseline,
      // and glyphs rise ~cap-height above it).
      const lastBaseline = startY + (rowCount - 1) * lh;
      const lineY = lastBaseline + lh * 0.4;
      doc.setDrawColor(220);
      doc.line(left, lineY, right, lineY);
      y = lineY + lh;
    };

    drawRow(columns.map((c) => c.label), { bold: true });
    rows.forEach((row) => drawRow(columns.map((c) => row[c.key])));

    if (totalLabel && totalValue != null) {
      ensureSpace(lh + 4);
      doc.setFont(FONT, "bold");
      doc.setFontSize(11);
      doc.setTextColor(30);
      doc.text(`${totalLabel}: ${totalValue}`, right, y, { align: "right" });
      y += lh + 6;
    }
  };

  const columnX = (fractions) => {
    let cursor = left;
    return fractions.map((f) => {
      const x = cursor;
      cursor += f * contentWidth;
      return { x, width: f * contentWidth };
    });
  };

  // ---- Header -----------------------------------------------------------
  writeLine("Lucille Graham Trust", { size: 18, style: "bold" });
  writeLine("Grant Application Form", { size: 13 });
  const dateStr = new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(`Submitted: ${dateStr}`, right, TOP_MARGIN, { align: "right" });
  doc.setTextColor(30);
  y += 6;
  doc.setDrawColor(120);
  doc.line(left, y, right, y);
  y += 10;

  // ---- Applicant details ------------------------------------------------
  section("Applicant details");
  field("Organisation", fields.organisation);
  field("Address", fields.address);
  field("Charity / company no.", fields.charity);
  field("Contact name", fields.name);
  field("Position / role", fields.position);
  field("Telephone", fields.tel);
  field("Email", fields.email);
  field("Website", fields.website);

  // ---- Funding ----------------------------------------------------------
  section("Funding");
  field("Amount requested", fields.fundingrequested ? formatMoney(fields.fundingrequested) : fields.fundingrequested);
  field("Public liability insurance", fields.liability);
  field("Safeguarding policy", fields.safeguarding);

  // ---- Narrative --------------------------------------------------------
  section("About your organisation");
  writeBlock(fields.about);
  section("Short project description");
  writeBlock(fields.project);
  section("Timeframe");
  writeBlock(fields.timeframe);
  section("Who will your project reach?");
  writeBlock(fields.outreach);
  section("Demand for your work");
  writeBlock(fields.demand);
  section("Project impact and outcomes");
  writeBlock(fields.impact);
  section("Evaluation");
  writeBlock(fields.evaluation);

  // ---- Budget: expenses -------------------------------------------------
  section("Budget — Expenses");
  const expCols = columnX([0.35, 0.45, 0.2]).map((c, i) => ({
    ...c,
    key: ["Expenditure", "Details", "Cost"][i],
    label: ["Expenditure", "Details", "Cost (£)"][i],
    align: i === 2 ? "right" : "left",
  }));
  table(expCols, expenses.map((e) => ({ ...e, Cost: formatMoney(e.Cost) })), {
    totalLabel: "Total expenses",
    totalValue: formatMoney(totalExpense),
  });

  // ---- Budget: income ---------------------------------------------------
  section("Budget — Income");
  const incCols = columnX([0.45, 0.25, 0.3]).map((c, i) => ({
    ...c,
    key: ["Income", "Confirmed", "Amount"][i],
    label: ["Income source", "Status", "Amount (£)"][i],
    align: i === 2 ? "right" : "left",
  }));
  table(incCols, income.map((row) => ({ ...row, Amount: formatMoney(row.Amount) })), {
    totalLabel: "Total income",
    totalValue: formatMoney(totalIncome),
  });

  ensureSpace(lineHeightFor(11) * 3);
  y += 4;
  writeLine(`Confirmed income: ${formatMoney(confirmedIncome)}`, { size: 11 });
  writeLine(`Pending income: ${formatMoney(pendingIncome)}`, { size: 11 });

  // ---- Document title ----------------------------------------------------
  // Shown by PDF readers in the tab/window title. A colon is fine in the
  // metadata title (unlike the attachment filename, which uses a hyphen).
  doc.setProperties({
    title: fields.organisation ? `Application: ${fields.organisation}` : "Application",
  });

  // ---- Page numbers -----------------------------------------------------
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor(150);
    doc.text(`Page ${i} of ${pageCount}`, right, pageHeight - 20, { align: "right" });
  }

  return doc.output("blob");
};

export default generateApplicationPdf;
